import React, {useMemo, useCallback, useState} from 'react';
import {Grid, GridCol, Button, PageContainer} from '@flixbus/honeycomb-react';
import {
  IconOfferSolid,
  IconEditSolid,
  IconDeleteSolid,
  IconDelete,
  Icon
} from '@flixbus/honeycomb-icons-react';
import ContentLoader from '@/Pages/Common/ContentLoader';
import {confirm} from '@/Pages/Common/Confirm';
import {useToast} from '@/Pages/Common/toasts-container';
import {Table, TableAction, TableHeader, TableRow} from '@/components/Table';
import {initCountryObject} from '../store/data';
import {Country} from '../store/types';
import {useCountries} from '../hooks/useCountries';
import {useDeleteCountry} from '../hooks/useDeleteCountry';
import CountryPanel from './CountryPanel/CountryPanel';
import {CountryPanelFormData} from './CountryPanel/CountryPanel.types';
import CountryCodesWithTooltip from './CountryCodesWithTooltip/CountryCodesWithTooltip';

const CountriesPage: React.FC = () => {
  const {countries, refetchCountries} = useCountries();
  const {addToast} = useToast();
  const {deleteCountry} = useDeleteCountry();

  const [country, setCountry] = useState<CountryPanelFormData | null>(null);
  const onClosePanel = useCallback(
    (country?: Country) => {
      // reset panel
      setCountry(null);

      // refetch countries if a country was successfully created or updated
      if (country) refetchCountries();
    },
    [refetchCountries]
  );

  const handleDeleteSuccess = useCallback(
    (country: Country) => {
      addToast({
        type: 'success',
        content: `Country ${country.name} deleted successfully`
      });
    },
    [addToast]
  );

  const handleDeleteFailure = useCallback(
    (errorMessage: string) => {
      addToast({
        type: 'danger',
        content: errorMessage
      });
    },
    [addToast]
  );

  const deleteCountryAction = useCallback(
    async (countryId: string, countryName: string) => {
      const confirmed = await confirm({
        PopupIcon: IconDelete,
        title:
          'This action will also delete empty driving defaults and the driver salary periods defined. Delete this country?',
        message: `${countryName}`
      });

      if (confirmed && countryId) {
        deleteCountry(countryId, handleDeleteSuccess, handleDeleteFailure);
      }
    },
    [deleteCountry, handleDeleteSuccess, handleDeleteFailure]
  );

  const headers: TableHeader[] = useMemo(
    () => [
      {key: 'name', name: 'Country'},
      {key: 'businessRegion', name: 'Business region'},
      {key: 'currency', name: 'Currency'},
      {key: 'countryCodesWithTooltip', name: 'Country codes'},
      {key: 'action', name: ''}
    ],
    []
  );

  const rows: TableRow[] = useMemo(() => {
    if (!countries) return [];

    return countries.map(country => ({
      ...country,
      countryCodesWithTooltip: Array.isArray(country.countryCodes) ? (
        <CountryCodesWithTooltip codes={country.countryCodes} />
      ) : (
        country.countryCodes
      )
    }));
  }, [countries]);

  const actions: TableAction[] = useMemo(
    () => [
      {
        icon: IconEditSolid,
        onClick: country => setCountry(country as CountryPanelFormData)
      },
      {
        icon: IconDeleteSolid,
        onClick: country =>
          deleteCountryAction(country.id as string, country.name as string),
        appearance: 'danger'
      }
    ],
    [deleteCountryAction]
  );

  if (!countries || !countries.length) {
    return <ContentLoader />;
  }

  return (
    <PageContainer>
      <div className={`hcr-has-text-right hcr-space-4-top hcr-space-2-bottom`}>
        <Button onClick={() => setCountry(initCountryObject)}>
          <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
          Add country
        </Button>
      </div>
      <Grid>
        <GridCol size={12}>
          <Table headers={headers} rows={rows} actions={actions} />
        </GridCol>
      </Grid>
      <CountryPanel initialCountry={country} onClose={onClosePanel} />
    </PageContainer>
  );
};

export default React.memo(CountriesPage);
